import { CurrencyPipe, PercentPipe } from '@angular/common'
import {
    Component,
    computed,
    DestroyRef,
    EventEmitter,
    inject,
    input,
    Input,
    OnInit,
    Output,
    signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { RouterLink } from '@angular/router'

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap'
import { NgSelectModule } from '@ng-select/ng-select'

import { SmartquoteHelperService } from 'src/app/secure/modules/smartquote-edit/services/smartquote-helper.service'
import { CardComponent } from 'src/app/shared/components/card/card.component'
import { ConfirmationDialogService } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.service'
import { ButtonComponent } from 'src/app/shared/components/form-elements/button/button.component'
import { InputMoneyComponent } from 'src/app/shared/components/form-elements/input-money/input-money.component'
import { InputQuantityComponent } from 'src/app/shared/components/form-elements/input-quantity/input-quantity.component'
import { InputTextareaAiComponent } from 'src/app/shared/components/form-elements/input-textarea-ai/input-textarea-ai.component'
import { InputTextareaComponent } from 'src/app/shared/components/form-elements/input-textarea/input-textarea.component'
import { InputComponent } from 'src/app/shared/components/form-elements/input/input.component'
import { LabelComponent } from 'src/app/shared/components/form-elements/label/label.component'
import { PostcodeLookupComponent } from 'src/app/shared/components/form-elements/postcode-lookup/postcode-lookup.component'
import { SwitchComponent } from 'src/app/shared/components/form-elements/switch/switch.component'
import { PartsArenaPartFinderComponent } from 'src/app/shared/components/partsarena/part-finder/parts-arena-part-finder.component'
import { SmartquoteBespokeProductSearchComponent } from 'src/app/shared/components/smartquote/smartquote-bespoke-product-search/smartquote-bespoke-product-search.component'
import { SmartquoteFavouriteProductSearchPulloutComponent } from 'src/app/shared/components/smartquote/smartquote-favourite-product-search-pullout/smartquote-favourite-product-search-pullout.component'
import { WolseleyProductSearchPulloutComponent } from 'src/app/shared/components/wolseley/wolseley-product-search-pullout/wolseley-product-search-pullout.component'
import { PAPartlistSectionRow } from 'src/app/shared/interfaces/partsarena/pa-partslistsectionrow.interface'
import { PostcodeAddress } from 'src/app/shared/interfaces/postcode-lookup/postcode-address.interface'
import { SmartQuoteBespokeFavourite } from 'src/app/shared/interfaces/smartquote-bespoke-favourite/smartquote-bespoke-favourite.interface'
import { SmartQuoteFavourite } from 'src/app/shared/interfaces/smartquote-favourite/smartquote-favourite.interface'
import { SmartQuoteTemplate } from 'src/app/shared/interfaces/smartquote-template/smartquote-template.interface'
import { WolseleyProduct } from 'src/app/shared/interfaces/wolseley-product/wolseley-product.interface'

import { SmartQuoteLineForm } from '../../../helpers/smartquote-line-form.interface'

@Component({
    selector: 'sf-smartquote-details',
    imports: [
        CurrencyPipe,
        PercentPipe,
        ReactiveFormsModule,
        RouterLink,
        FontAwesomeModule,
        NgSelectModule,
        ButtonComponent,
        CardComponent,
        InputComponent,
        InputMoneyComponent,
        InputQuantityComponent,
        InputTextareaComponent,
        InputTextareaAiComponent,
        LabelComponent,
        PostcodeLookupComponent,
        SwitchComponent,
    ],
    templateUrl: './smartquote-details.component.html',
    styleUrl: './smartquote-details.component.scss',
})
export class SmartquoteDetailsComponent implements OnInit {
    #confirmationService = inject(ConfirmationDialogService)
    #destroyRef = inject(DestroyRef)
    #formBuilder = inject(FormBuilder)
    #offcanvas = inject(NgbOffcanvas)

    smartquoteHelperService = inject(SmartquoteHelperService)

    @Input() form!: FormGroup
    @Input() gcNumber: string | null = null
    @Input() markup = 1

    @Output() lineAdded = new EventEmitter<void>()
    @Output() changeDefaultImageUrl = new EventEmitter<string>()

    smartQuoteTemplates = input<SmartQuoteTemplate[]>([])

    existingWorkAddress = signal<string | null>(null)
    workAddress = computed(() => {
        const quote = this.smartquoteHelperService.existingSmartQuote()
        const items = [quote?.addressLine1, quote?.addressLine2, quote?.town, quote?.county, quote?.postcode]
        return items.filter(Boolean).join(', ')
    })

    ngOnInit(): void {
        this.form.valueChanges
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(({ workAddress }) => this.existingWorkAddress.set(workAddress))
    }

    get lineItems(): FormArray<SmartQuoteLineForm> {
        return this.form.get('lineItems') as FormArray<SmartQuoteLineForm>
    }

    selectAddress(address: PostcodeAddress): void {
        this.form.patchValue({
            addressLine1: address.envelopeAddress.line1,
            addressLine2: address.envelopeAddress.line2,
            town: address.envelopeAddress.town,
            postcode: address.envelopeAddress.postcode,
        })
    }

    onJobTypeChange(jobType: SmartQuoteTemplate): void {
        this.#confirmationService
            .confirm(
                'Are you sure?',
                'Are you sure you want to load this template? This will overwrite the job description field.'
            )
            .then((confirmed) => {
                if (confirmed) {
                    this.form.patchValue({
                        notes: jobType.description,
                    })
                }
            })
    }

    onAIGeneratedText(generatedText: string): void {}

    removeLineItem(index: number): void {
        this.lineItems.removeAt(index)
    }

    addLineItem(
        productId: number | null,
        catalogNumber: string | null,
        description: string,
        imageUrl: string,
        mpn: string,
        quantity: number,
        cost: number
    ): FormGroup {
        return this.#formBuilder.group({
            id: [0],
            productId: [productId],
            catalogNumber: [catalogNumber],
            description: [description],
            imageUrl: [imageUrl],
            mpn: [mpn],
            quantity: [quantity, [Validators.min(1)]],
            cost: [cost * this.markup, [Validators.required]],
            terms: [0],
        })
    }

    addItem(
        productId: number | null,
        catalogNumber: string | null,
        description: string,
        imageUrl: string,
        mpn: string,
        quantity: number,
        cost: number
    ): void {
        const exists = this.lineItems.value.some((line) => line.catalogNumber && line.catalogNumber === catalogNumber)

        if (exists) {
            const line = this.lineItems.controls.find((line) => line.value.catalogNumber === catalogNumber)
            if (line) {
                const quantity = line.controls.quantity
                quantity.setValue(quantity.value! + 1)
            }
        } else {
            this.lineItems.push(this.addLineItem(productId, catalogNumber, description, imageUrl, mpn, quantity, cost))
        }
    }

    openPartsArenaSearch(): void {
        const ref = this.#offcanvas.open(PartsArenaPartFinderComponent, { position: 'end' })

        ref.componentInstance.gcNumber = this.gcNumber
        ref.componentInstance.onPartSelected
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((part: PAPartlistSectionRow) => {
                this.addItem(null, part.catalogNumber, part.description, '', '', 1, part.trade)
                this.lineAdded.emit()
            })
    }

    openWolseleySearch(): void {
        const ref = this.#offcanvas.open(WolseleyProductSearchPulloutComponent, { position: 'end' })
        ref.componentInstance.partSelected
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((part: WolseleyProduct) => {
                this.addItem(
                    part.id,
                    part.catalogNumber,
                    part.name,
                    part.imageUrl,
                    part.manufacturerPartNumber,
                    1,
                    part.price
                )
                this.lineAdded.emit()
            })
    }

    openSmartquoteBespokeFavouriteSearch(): void {
        const ref = this.#offcanvas.open(SmartquoteBespokeProductSearchComponent, { position: 'end' })

        ref.componentInstance.partSelected
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((part: SmartQuoteBespokeFavourite) => {
                this.addItem(null, null, part.description, '', part.mpn, 1, part.cost)
            })
    }

    openSmartQuoteFavouriteSearch(): void {
        const ref = this.#offcanvas.open(SmartquoteFavouriteProductSearchPulloutComponent, { position: 'end' })

        ref.componentInstance.partSelected
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((part: SmartQuoteFavourite) => {
                this.addItem(
                    part.productId,
                    part.catalogNumber,
                    part.name,
                    '',
                    part.manufacturerPartNumber,
                    1,
                    part.price || 0
                )
                this.lineAdded.emit()
            })
    }

    toggleFreeOfCharge(): void {
        const control = this.form.get('freeOfCharge')
        control?.setValue(!control.value, { emitEvent: true })
    }

    toggleCombinedLines(): void {
        const control = this.form.get('combinedLines')
        control?.setValue(!control.value, { emitEvent: true })
    }
}
